import { toFailedValidations } from './lookupBlocking';
import { FACE_MATCH_FAILURE, FACE_MATCH_STATE, blocksSubmitOnFaceMatch } from './faceMatchEnforcement';
import { OCR_ISSUE, OCR_TARGET_CONFIG, buildOcrVerificationSnapshot } from './ocrVerification';

// Also the `type` the BE stores each override under.
export const SUBMIT_BLOCKER = {
    OCR_VERIFICATION: 'OCR_VERIFICATION',
    FACE_MATCH: 'FACE_MATCH',
    LOOKUP: 'LOOKUP',
    RELAY: 'RELAY',
};

// Top-to-bottom order of the dialog sections and of the overrides on the wire.
const SUBMIT_BLOCKER_ORDER = [SUBMIT_BLOCKER.OCR_VERIFICATION, SUBMIT_BLOCKER.FACE_MATCH, SUBMIT_BLOCKER.LOOKUP, SUBMIT_BLOCKER.RELAY];

export const MAX_OVERRIDE_REASON_LENGTH = 1000;

export const SUBMIT_BLOCK_DIALOG = {
    title: 'Submit with unresolved issues?',
    description: "One or more required actions are not complete. You can still submit after providing a reason where it's needed.",
    confirmLabel: 'Submit anyway',
    // Once a retry satisfied the face match and nothing else still blocks, nothing is overridden any more.
    confirmLabelResolved: 'Submit',
    confirmShortcut: 'Enter',
    cancelLabel: 'Cancel',
    cancelShortcut: 'Esc',
    closeLabel: 'close',
    missingReason: 'Add a reason to continue',
};

export const SUBMIT_BLOCKER_SECTIONS = {
    [SUBMIT_BLOCKER.OCR_VERIFICATION]: {
        lead: 'OCR Verification is required',
        body: 'to ensure that trailer and seal numbers match the values detected in the BOL document and on the vehicle.',
        reasonLabel: 'Reason for submitting without a valid OCR Verification',
        reasonPlaceholder: 'Briefly explain why the OCR verification is not valid.',
    },
    // No static body: the face match text depends on what the latest attempt did, see getSubmitBlockerMessage.
    [SUBMIT_BLOCKER.FACE_MATCH]: {
        lead: 'Face match',
        reasonLabel: 'Reason for submitting without face match',
        reasonPlaceholder: 'Briefly explain why face match is not satisfied.',
    },
    [SUBMIT_BLOCKER.LOOKUP]: {
        lead: 'Validation errors',
        body: 'are highlighted on some fields. These fields contain values that don’t match the expected options.',
        reasonLabel: 'Reason for submitting with validation errors',
        reasonPlaceholder: 'Briefly explain why these values should be submitted.',
    },
    // No reason field: overriding the relay is a confirmation only (BEAST-14538).
    [SUBMIT_BLOCKER.RELAY]: {
        lead: 'Gate relays',
        body: 'are not used for this transaction. Before submitting, make sure the gate has been opened and fully closed.',
    },
};

// Blockers the agent overrides without typing a reason. They are still sent as an override, with no `reason`,
// for the audit and so a retried parked submit does not block on them again.
const BLOCKERS_WITHOUT_REASON = [SUBMIT_BLOCKER.RELAY];

export const blockerRequiresReason = (blocker) => !BLOCKERS_WITHOUT_REASON.includes(blocker);

// When OCR is the only blocker the dialog takes its own title and asks for the reason in plain text (Figma 16619:4190).
export const OCR_ONLY_SUBMIT_BLOCK_DIALOG = {
    title: 'Submit without a valid OCR Verification?',
    request:
        'Please provide a reason for submitting despite the mismatch between the entered numbers and the OCR results from the BOL document and vehicle.',
};

export const isOcrOnlySubmitBlock = (blockers) => blockers?.length === 1 && blockers[0] === SUBMIT_BLOCKER.OCR_VERIFICATION;

// When the relay is the only blocker the dialog takes the relay title and body, with no card and no reason field.
export const RELAY_ONLY_SUBMIT_BLOCK_DIALOG = {
    title: 'Submit without using relays?',
    body: "You haven't used the gate relays for this transaction. Before submitting, make sure the gate has been opened and fully closed.",
};

export const isRelayOnlySubmitBlock = (blockers) => blockers?.length === 1 && blockers[0] === SUBMIT_BLOCKER.RELAY;

const OCR_ISSUE_TEXT = {
    [OCR_ISSUE.IN_PROGRESS]: () => 'text is still being read from a photo.',
    [OCR_ISSUE.NOT_COMPLETED]: () => 'not read from the BOL document and the vehicle yet.',
    [OCR_ISSUE.INCOMPLETE]: () => 'could not be verified: a value is missing or unreadable.',
    [OCR_ISSUE.MISMATCH]: () => 'the BOL document and the vehicle do not match.',
    [OCR_ISSUE.FIELD_DISCREPANCY]: ({ fieldValue }) =>
        fieldValue ? `the entered value ${fieldValue} differs from the verified number.` : 'the field does not hold the verified number.',
};

// One line per OCR check that does not pass, naming the field, so the agent knows what to fix or explain.
export const getOcrIssueLines = (issues) =>
    (issues || []).map((issue) => ({ label: OCR_TARGET_CONFIG[issue.target].label, text: OCR_ISSUE_TEXT[issue.kind](issue) }));

// The dialog's OCR lines. After a BE rejection the form's own checks normally explain it (the gate mode was read
// again); only when they find nothing is the BE message the line, with no field to name.
export const getOcrDialogLines = (ocrSubmitState) => {
    const lines = getOcrIssueLines(ocrSubmitState?.issues);
    if (lines.length || !ocrSubmitState?.serverRejection) return lines;
    return [{ label: null, text: ocrSubmitState.serverRejection }];
};

// Every message of the BE's OcrVerificationExceptionCode starts with this. The 400 body carries no code
// (ErrorResult { error, details }), so the prefix is the contract with TruckTransactionOcrVerificationService.
const OCR_REJECTION_PREFIX = 'OCR verification';
// VALUE_TOO_LONG: the BE checks the lengths even with an override, so a reason cannot lift it.
const OCR_REJECTION_NOT_OVERRIDABLE_PREFIX = 'OCR verification values must not exceed';

/** The BE message of a submit rejected over the OCR verification that an OCR_VERIFICATION override lifts, else null. */
export function getOverridableOcrRejection(err) {
    const message = err?.response?.status === 400 ? err.response.data?.error : null;
    if (typeof message !== 'string' || !message.startsWith(OCR_REJECTION_PREFIX)) return null;
    return message.startsWith(OCR_REJECTION_NOT_OVERRIDABLE_PREFIX) ? null : message;
}

// The live OCR state as the dialog of a BE rejection sees it: the rejection keeps the OCR blocker until overridden.
export const withOcrServerRejection = (ocrSubmitState, serverRejection) =>
    ocrSubmitState && serverRejection ? { ...ocrSubmitState, serverRejection } : ocrSubmitState;

const ocrStillBlocks = (ocrSubmitState) => !ocrSubmitState || ocrSubmitState.issues.length > 0 || !!ocrSubmitState.serverRejection;

const plain = (text) => ({ text, emphasis: false });
const percent = (value) => ({ text: `${value}%`, emphasis: true });

const FACE_MATCH_FAILURE_TEXT = {
    [FACE_MATCH_FAILURE.NO_FACE_DETECTED]: 'no face was detected in the images',
    [FACE_MATCH_FAILURE.IMAGE_TOO_LARGE]: 'the images are too large',
    [FACE_MATCH_FAILURE.INVALID_IMAGE_FORMAT]: 'the image format is not supported',
    [FACE_MATCH_FAILURE.SNAPSHOT_ERROR]: 'the camera snapshots could not be captured',
    [FACE_MATCH_FAILURE.TIMEOUT]: 'the face match service did not respond in time',
    [FACE_MATCH_FAILURE.SERVICE_ERROR]: 'the face match service returned an error',
};

function getFaceMatchMessage({ state, score, threshold, failure } = {}) {
    const requirement = [plain(' This gate requires a score of at least '), percent(threshold), plain('.')];

    switch (state) {
        case FACE_MATCH_STATE.IN_PROGRESS:
            return [plain('is still running. The result will appear here in a few seconds.')];
        case FACE_MATCH_STATE.SATISFIED:
            return [
                plain('scored '),
                percent(score),
                plain(', which meets the '),
                percent(threshold),
                plain(' this gate requires. No reason is needed.'),
            ];
        case FACE_MATCH_STATE.BELOW_THRESHOLD:
            return [plain('scored '), percent(score), plain(', below the '), percent(threshold), plain(' this gate requires.')];
        case FACE_MATCH_STATE.FAILED:
            return [
                plain(
                    `could not produce a result: ${FACE_MATCH_FAILURE_TEXT[failure] ?? FACE_MATCH_FAILURE_TEXT[FACE_MATCH_FAILURE.SERVICE_ERROR]}.`
                ),
                ...requirement,
            ];
        default:
            return [plain('has not been run.'), ...requirement];
    }
}

// The text after the bold lead, as segments so the dialog can bold the numbers without utils knowing JSX.
export const getSubmitBlockerMessage = (blocker, details) =>
    blocker === SUBMIT_BLOCKER.FACE_MATCH ? getFaceMatchMessage(details) : [plain(SUBMIT_BLOCKER_SECTIONS[blocker]?.body ?? '')];

export const getReasonCounterText = (reason = '') => `${reason.length}/${MAX_OVERRIDE_REASON_LENGTH}`;

export const normalizeOverrideReason = (reason) => (typeof reason === 'string' ? reason.trim() : '');

export const isOverrideReasonValid = (reason) => normalizeOverrideReason(reason).length > 0;

export const isFaceMatchRunning = (blockers, faceMatchStatus) =>
    (blockers || []).includes(SUBMIT_BLOCKER.FACE_MATCH) && faceMatchStatus?.state === FACE_MATCH_STATE.IN_PROGRESS;

// Face match and OCR blockers drop out once the live state satisfies them. With no live state they still block.
const stillBlocks = (blocker, faceMatchStatus, ocrSubmitState) => {
    if (blocker === SUBMIT_BLOCKER.FACE_MATCH) return !faceMatchStatus || blocksSubmitOnFaceMatch(faceMatchStatus);
    if (blocker === SUBMIT_BLOCKER.OCR_VERIFICATION) return ocrStillBlocks(ocrSubmitState);
    return true;
};

// Every blocker the submit still overrides, the ones without a reason field (relay) included.
export const getBlockersStillBlocking = (blockers, faceMatchStatus, ocrSubmitState) =>
    (blockers || []).filter((blocker) => stillBlocks(blocker, faceMatchStatus, ocrSubmitState));

export const getBlockersNeedingReason = (blockers, faceMatchStatus, ocrSubmitState) =>
    getBlockersStillBlocking(blockers, faceMatchStatus, ocrSubmitState).filter(blockerRequiresReason);

export const getMissingReasonBlockers = (blockers, reasons, faceMatchStatus, ocrSubmitState) =>
    getBlockersNeedingReason(blockers, faceMatchStatus, ocrSubmitState).filter((blocker) => !isOverrideReasonValid(reasons?.[blocker]));

export const findBlockSubmissionOverride = (blockSubmissionOverrides, type) =>
    (blockSubmissionOverrides || []).find((override) => override?.type === type) || null;

export function buildLookupBlockSubmissionOverride(blockingValidations, reason) {
    const trimmedReason = normalizeOverrideReason(reason);
    const failedValidations = toFailedValidations(blockingValidations);
    if (!trimmedReason || failedValidations.length === 0) return null;
    return { type: SUBMIT_BLOCKER.LOOKUP, reason: trimmedReason, resultSnapshot: { failedValidations } };
}

// No reason: the relay override is a confirmation only, the BE stores it with a null reason.
export function buildRelayBlockSubmissionOverride({ relaySubmitBlocked, relayEnforcement }) {
    if (!relaySubmitBlocked) return null;
    return { type: SUBMIT_BLOCKER.RELAY, resultSnapshot: { relayEnforcement } };
}

export function buildFaceMatchBlockSubmissionOverride(faceMatchStatus, reason) {
    const trimmedReason = normalizeOverrideReason(reason);
    if (!trimmedReason || !blocksSubmitOnFaceMatch(faceMatchStatus)) return null;
    const { state, score, threshold } = faceMatchStatus;
    return { type: SUBMIT_BLOCKER.FACE_MATCH, reason: trimmedReason, resultSnapshot: { state, score, threshold } };
}

// One reason covers every OCR issue of this submit. After a BE rejection its message is kept for the audit.
export function buildOcrVerificationBlockSubmissionOverride(ocrSubmitState, reason) {
    const trimmedReason = normalizeOverrideReason(reason);
    if (!trimmedReason || !ocrSubmitState || !ocrStillBlocks(ocrSubmitState)) return null;
    const { serverRejection } = ocrSubmitState;
    return {
        type: SUBMIT_BLOCKER.OCR_VERIFICATION,
        reason: trimmedReason,
        resultSnapshot: { ...buildOcrVerificationSnapshot(ocrSubmitState), ...(serverRejection && { serverRejection }) },
    };
}

const OVERRIDE_BUILDERS = {
    [SUBMIT_BLOCKER.OCR_VERIFICATION]: (liveState, reason) => buildOcrVerificationBlockSubmissionOverride(liveState.ocrSubmitState, reason),
    [SUBMIT_BLOCKER.FACE_MATCH]: (liveState, reason) => buildFaceMatchBlockSubmissionOverride(liveState.faceMatchStatus, reason),
    [SUBMIT_BLOCKER.LOOKUP]: (liveState, reason) => buildLookupBlockSubmissionOverride(liveState.blockingValidations, reason),
    [SUBMIT_BLOCKER.RELAY]: (liveState) => buildRelayBlockSubmissionOverride(liveState),
};

// `carried` holds the overrides of an earlier round of the dialog, still riding in the parked submit.
// A blocker answered again drops its carried entry, so a stale one never travels next to the new one,
// and a blocker that cleared in the live state is sent with no override at all.
export function buildBlockSubmissionOverrides({ carried = null, blockers = [], reasons = {}, liveState = {} }) {
    const overridesByType = new Map((carried || []).map((override) => [override.type, override]));
    blockers.forEach((blocker) => {
        overridesByType.delete(blocker);
        const override = OVERRIDE_BUILDERS[blocker]?.(liveState, reasons?.[blocker]);
        if (override) overridesByType.set(blocker, override);
    });
    return SUBMIT_BLOCKER_ORDER.map((type) => overridesByType.get(type)).filter(Boolean);
}

export const hasOpenSubmitBlockDialog = (formsSlice) => Object.values(formsSlice?.forms || {}).some((form) => !!form?.submitBlockDialog);
